import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

/**
 * ToastNotification - A simple toast notification component
 * @param {Object} props - Component props
 * @param {string} props.type - Toast type: 'success', 'error', 'info'
 * @param {string} props.message - Toast message
 * @param {boolean} props.show - Whether to show the toast
 * @param {Function} props.onClose - Function to call when toast closes
 * @param {number} props.duration - Auto-hide duration in milliseconds (0 = no auto-hide)
 * @param {string} props.position - Toast position: 'top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center'
 */
const ToastNotification = ({
  type = 'info',
  message,
  show = false,
  onClose,
  duration = 5000,
  position = 'top-right',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setIsAnimating(true);

      // Auto-hide after duration
      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);

        return () => clearTimeout(timer);
      }
    }
  }, [show, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} className="text-green-500" />;
      case 'error':
        return <AlertCircle size={20} className="text-red-500" />;
      default:
        return <Info size={20} className="text-blue-500" />;
    }
  };

  // Get colors based on type
  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  // Get position classes
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-4 right-4';
      default:
        return 'top-4 right-4';
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed z-50 ${getPositionClasses()} transition-all duration-300 ease-in-out ${
        isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-sm ${getColors()}`}
      >
        {getIcon()}
        <p className="flex-1 text-sm font-medium">{message}</p>
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
          aria-label="Close notification"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

/**
 * ToastContainer - Container component for managing multiple toasts
 */
export const ToastContainer = ({ toasts = [], onRemoveToast, position = 'top-right' }) => {
  const getContainerClasses = () => {
    const baseClasses = 'fixed z-50 flex flex-col gap-2';

    switch (position) {
      case 'top-left':
        return `${baseClasses} top-4 left-4`;
      case 'top-center':
        return `${baseClasses} top-4 left-1/2 transform -translate-x-1/2`;
      case 'top-right':
        return `${baseClasses} top-4 right-4`;
      case 'bottom-left':
        return `${baseClasses} bottom-4 left-4`;
      case 'bottom-center':
        return `${baseClasses} bottom-4 left-1/2 transform -translate-x-1/2`;
      case 'bottom-right':
        return `${baseClasses} bottom-4 right-4`;
      default:
        return `${baseClasses} top-4 right-4`;
    }
  };

  return (
    <div className={getContainerClasses()}>
      {toasts.map((toast) => (
        <ToastNotification
          key={toast.id}
          type={toast.type}
          message={toast.message}
          show={toast.show}
          onClose={() => onRemoveToast(toast.id)}
          duration={toast.duration}
          position="static" // Override position for container
        />
      ))}
    </div>
  );
};

/**
 * Hook for managing toast notifications
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      show: true,
      duration: 5000,
      ...toast,
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    // Auto-remove after duration + animation time
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration + 300);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) =>
        toast.id === id ? { ...toast, show: false } : toast
      )
    );

    // Remove from array after animation
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 300);
  };

  const clearAllToasts = () => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) => ({ ...toast, show: false }))
    );

    setTimeout(() => {
      setToasts([]);
    }, 300);
  };

  // Convenience methods
  const success = (message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  };

  const error = (message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  };

  const info = (message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  };

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    info,
  };
};

export default ToastNotification;
